import { Link, useLocation, useNavigate } from "@remix-run/react";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "~/components/ui/popover";
import { cn, getMenuHref } from "~/lib/utils";
import { useShopCartStore } from "~/stores/shopCart";

interface ShopCartIconProps {
  className?: string;
}

export const ShopCartIcon = ({ className }: ShopCartIconProps = {}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { shopCart } = useShopCartStore();
  const [itemCount, setItemCount] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const [shouldBounce, setShouldBounce] = useState(false);
  const [open, setOpen] = useState(false);
  const prevItemCountRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const iconRef = useRef<HTMLDivElement>(null);

  const isCartPage =
    location.pathname.includes("/shop/cart") ||
    location.pathname.includes("/cart");

  useEffect(() => {
    setIsClient(true);
    const newCount = shopCart.item_count;

    if (isCartPage) {
      setOpen(false);
      prevItemCountRef.current = newCount;
      setItemCount(newCount);
      isInitialMountRef.current = false;
      return;
    }

    if (isInitialMountRef.current) {
      prevItemCountRef.current = newCount;
      setItemCount(newCount);
      isInitialMountRef.current = false;
      return;
    }

    if (newCount > prevItemCountRef.current) {
      if (prevItemCountRef.current === 0) {
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
      } else {
        setShouldBounce(true);
        setTimeout(() => setShouldBounce(false), 600);
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
      }
    }

    prevItemCountRef.current = newCount;
    setItemCount(newCount);
  }, [shopCart, isCartPage]);

  const handleGoToCart = () => {
    setOpen(false);
    navigate(getMenuHref(i18n.language, "/shop/cart"));
  };

  return (
    <div ref={iconRef} className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <Link
            to={getMenuHref(i18n.language, "/shop/cart")}
            className={cn(
              "relative p-2 transition-colors block",
              className || "text-secondary"
            )}
          >
            <motion.div
              className="w-5 h-5"
              animate={
                shouldBounce
                  ? {
                      scale: [1, 1.2, 1],
                    }
                  : {}
              }
              transition={{
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              <svg
                width="31"
                height="28"
                viewBox="0 0 31 28"
                className="w-5 h-5"
                fill="currentColor"
              >
                <path
                  d="M9.98788 18.6667H9.98909C9.98909 18.6667 9.99151 18.6667 9.99272 18.6667H26.4592C26.8648 18.6667 27.2209 18.3904 27.3323 17.9897L30.9651 4.92302C31.0432 4.64116 30.9887 4.33876 30.8168 4.1048C30.6448 3.87084 30.3766 3.73333 30.0914 3.73333H7.89355L7.24448 0.731111C7.15185 0.304267 6.78372 0 6.35807 0H0.908209C0.406272 0 0 0.417511 0 0.933333C0 1.44916 0.406272 1.86667 0.908209 1.86667H5.62908C5.74412 2.39867 8.73576 16.235 8.90832 17.0308C7.9432 17.462 7.26628 18.4508 7.26628 19.6C7.26628 21.1437 8.48873 22.4 9.99091 22.4H26.4598C26.9617 22.4 27.368 21.9825 27.368 21.4667C27.368 20.9508 26.9617 20.5333 26.4598 20.5333H9.9903C9.48958 20.5333 9.08209 20.1146 9.08209 19.6C9.08209 19.0854 9.48837 18.6679 9.98788 18.6667ZM8.2968 5.6H28.8877L25.7738 16.8H10.7187L8.2968 5.6ZM9.08209 25.2C9.08209 26.7437 10.3045 28 11.8067 28C13.3089 28 14.5314 26.7437 14.5314 25.2C14.5314 23.6563 13.3089 22.4 11.8067 22.4C10.3045 22.4 9.08209 23.6563 9.08209 25.2ZM11.8067 24.2667C12.3074 24.2667 12.7149 24.6854 12.7149 25.2C12.7149 25.7146 12.3074 26.1333 11.8067 26.1333C11.306 26.1333 10.8985 25.7146 10.8985 25.2C10.8985 24.6854 11.306 24.2667 11.8067 24.2667ZM21.9181 25.2C21.9181 26.7437 23.1406 28 24.6427 28C26.1449 28 27.3674 26.7437 27.3674 25.2C27.3674 23.6563 26.1449 22.4 24.6427 22.4C23.1406 22.4 21.9181 23.6563 21.9181 25.2ZM24.6427 24.2667C25.1435 24.2667 25.551 24.6854 25.551 25.2C25.551 25.7146 25.1435 26.1333 24.6427 26.1333C24.142 26.1333 23.7345 25.7146 23.7345 25.2C23.7345 24.6854 24.142 24.2667 24.6427 24.2667Z"
                  fill="currentColor"
                />
              </svg>
            </motion.div>

            {isClient && itemCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 30,
                }}
                className="absolute -top-0 -right-0 bg-[#FF3838] text-white text-xs rounded-full h-4 w-4 flex items-center justify-center"
              >
                {itemCount}
              </motion.span>
            )}
          </Link>
        </PopoverAnchor>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="w-72 p-0 bg-[#f7ece3]"
        >
          {shopCart.items.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-700">{t("Carrello vuoto")}</p>
            </div>
          ) : (
            <>
              <div className="max-h-[300px] overflow-y-auto grid grid-cols-1 gap-2 pt-4">
                {shopCart.items.map((item) => (
                  <div key={item.id} className="flex px-4">
                    <div className="flex-shrink-0">
                      <img
                        src={item.image || "/images/placeholder.png"}
                        alt={item.name}
                        className="w-20 h-20 object-cover"
                      />
                    </div>
                    <div className="flex-1 flex flex-col justify-between pl-4">
                      <div className="text-secondary font-medium text-sm leading-tight mb-1">
                        {item.name.split("\n").map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}
                      </div>
                      <div className="text-black text-sm mb-2">
                        {item.quantity} X{" "}
                        {item.price.toFixed(2).replace(".", ",")} €
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 pt-2">
                <button
                  onClick={handleGoToCart}
                  className="w-full py-2 bg-[#fffbf7] border border-secondary text-black text-sm font-medium hover:bg-secondary hover:text-white transition-colors"
                >
                  {t("Vai al carrello")}
                </button>
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};
